import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, ASPECT_RATIOS, indexById, refreshPosterSlides } from "@/lib/api";
import { SlideShow } from "@/components/SlideShow";
import { Button } from "@/components/ui/button";
import { X, Pause, Play, Maximize, Loader2 } from "lucide-react";

const padFor = (ratio) => ASPECT_RATIOS.find((r) => r.value === ratio)?.pad || "56.25%";

export default function Player() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    Promise.all([api.get(`/banners/${id}`), api.get("/posters").catch(() => ({ data: [] }))])
      .then(([b, pr]) => {
        const data = b.data;
        data.images = refreshPosterSlides(data.images, indexById(pr.data));
        setBanner(data);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    let timer;
    const reveal = () => {
      setShowControls(true);
      clearTimeout(timer);
      timer = setTimeout(() => setShowControls(false), 2500);
    };
    reveal();
    window.addEventListener("mousemove", reveal);
    window.addEventListener("touchstart", reveal);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", reveal);
      window.removeEventListener("touchstart", reveal);
    };
  }, []);

  const goFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-black text-white">
        <p className="mb-4 font-display text-lg">Bandeau introuvable</p>
        <Button onClick={() => navigate("/")} data-testid="player-back-home">Retour</Button>
      </div>
    );
  }

  if (!banner) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black text-white">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-black" data-testid="player-root">
      <div className="relative w-full" style={{ maxHeight: "100vh" }}>
        <div className="relative mx-auto w-full" style={{ paddingTop: padFor(banner.aspect_ratio), maxHeight: "100vh" }}>
          <SlideShow images={banner.images} playing={playing} loop={banner.loop} />
        </div>
      </div>

      <div
        className={`fixed left-1/2 top-6 z-50 -translate-x-1/2 transition-opacity duration-300 ${
          showControls ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div className="flex items-center gap-2 rounded-full border border-white/15 bg-black/60 px-3 py-2 backdrop-blur-xl">
          <span className="px-2 font-display text-sm font-semibold text-white">{banner.name}</span>
          <Button
            size="icon" variant="ghost" className="h-9 w-9 rounded-full text-white hover:bg-white/15 hover:text-white"
            onClick={() => setPlaying((p) => !p)} data-testid="player-playpause-button"
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button
            size="icon" variant="ghost" className="h-9 w-9 rounded-full text-white hover:bg-white/15 hover:text-white"
            onClick={goFullscreen} data-testid="player-fullscreen-button"
          >
            <Maximize className="h-4 w-4" />
          </Button>
          <Button
            size="icon" variant="ghost" className="h-9 w-9 rounded-full text-white hover:bg-white/15 hover:text-white"
            onClick={() => navigate("/")} data-testid="player-close-button"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
